/*
 * Authors: mariushirjanu
 * Created: 26/08/2021
 */
import { util } from '../../shared/util';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Subscription } from 'rxjs';
import { STATE_COMPANYINFO } from '../../shared/app-states';
import { BodyClassService } from '../../shared/services/body-class.service';
import { CompanyDataService, CompanyType } from '../../shared/company/company-data.service';
import { ICompanyExtended, IObject } from '../../../type_definitions/datatypes';
import { TranslationService } from '../../shared/translation/translation.service';
import { GaMessageService } from '../../shared/message/ga-message.service';
import { StateService } from '@uirouter/angular';
import { GaEventService, IGAEvent } from '../../shared/services/ga-event.service';
import { CompanyInfoService } from './company-info/company-info.service';
import { EVENT_COMPANY_UPDATED, EVENT_DUPLICATE_COMPANY_SELECTED } from '../../shared/app-events';
import { GaWizardService } from '../../shared/wizard/ga-wizard.service';
import { GaWizard } from '../../shared/wizard/ga-wizard';
import { CompanyInfoComponent } from './company-info/company-info.component';
import { WIZARD_COMPANY } from '../../shared/app-wizards';
import { ITranslateText } from '../../shared/wizard/wizard-wrapper.component';

const SAVE_ACTION:string = 'save';
const CANCEL_ACTION:string = 'cancel';

@Component({
  selector: 'company',
  template: `
    <wizard-wrapper [wizardTitle]="title" [loading]="loading" [wizardId]="wizardId" [hideHeader]="showAsDialog" [showAsDialog]="showAsDialog"></wizard-wrapper>
  `
})
export class CompanyComponent implements OnInit, OnDestroy {
  @Input() public companyId:string;
  @Input() public companyName:string;
  @Input() public showAsDialog:boolean;
  @Input() public stateParams:IObject;

  @Output() public onSaved:EventEmitter<ICompanyExtended> = new EventEmitter<ICompanyExtended>();
  @Output() public onCancel:EventEmitter<void> = new EventEmitter<void>();

  private wizard:GaWizard;
  private eventHandler:Subscription;
  public title:string|ITranslateText;
  public error:string;
  public loading:ITranslateText;
  public wizardId:string = WIZARD_COMPANY;

  constructor(
    private bodyClassService:BodyClassService,
    private companyInfoService:CompanyInfoService,
    private companyDataService:CompanyDataService,
    private messageService:GaMessageService,
    private eventService:GaEventService,
    private stateService:StateService,
    private translationService:TranslationService,
    private wizardService:GaWizardService
  ) {
    this.wizard = this.wizardService.getInstance(this.wizardId);
    this.bodyClassService.add('company-profile');
  }

  public ngOnInit():void {
    this.start(this.companyId);
    this.listen();
  }

  private start(companyId?:string):Promise<ICompanyExtended> {
    if (!companyId) {
      return;
    }
    this.companyInfoService.setCompanyId(companyId);
    this.loading = {
      translateText: 'GA.LOADING',
      translateValues: { toload: 'GA.GENERAL.COMPANY' }
    };
    this.title = 'GA.GENERAL.COMPANY';

    if (this.companyName) {
      // pass company name and preselect customer as company type
      const companyData: any = {
        name: this.companyName,
        companyType: CompanyType.CUSTOMER
      };

      // when company name provided and it is to create new company then set active true
      if (companyId === CompanyInfoService.NEW_COMPANY) {
        companyData.active = true;
      }

      this.companyInfoService.setCompanyData(companyData);
    }

    return this.companyInfoService.getCompanyProfilePromise()
      .then((companyData:ICompanyExtended) => {
        this.initWizard();
        this.title = this.getWizardTitle(companyData?.name);
        return companyData;
      })
      .finally(() => {
        this.loading = undefined;
      });
  }

  private listen = ():void => {
    this.eventHandler = this.eventService.events.subscribe((event:IGAEvent) => {
      switch (event.name) {
        // receive events to load a company and decide if restarting the controller or replacing the location
        case EVENT_DUPLICATE_COMPANY_SELECTED: {
          const companyId:string = event.data?.toString();
          if (this.showAsDialog || companyId === this.companyId) {
            this.start(companyId);
          } else {
            this.wizard.clearCurrentValidator(); // remove form wizard validator before doing the redirect to the new state
            this.stateService.go(STATE_COMPANYINFO, { companyId }, { location: 'replace' });
          }
          break;
        }
      }
    });
  };

  private initWizard():void {
    this.wizard.steps([
      this.wizardService.createStep('companyinfo', 'GA.GENERAL.COMPANY', CompanyInfoComponent, STATE_COMPANYINFO, this.stateParams, undefined, true)
    ]);

    if (this.showAsDialog) {
      this.wizard.stateSynced = false;
    } else {
      this.wizard.sync();
    }

    this.wizard.actions([
      this.wizardService.createActionButton(
        SAVE_ACTION,
        this.translationService.instant('GA.BUTTONS.SAVE_LABEL', { label: 'GA.GENERAL.COMPANY' }),
        'btn btn-success',
        this.saveCompany
      ),
      this.wizardService.createActionButton(
        CANCEL_ACTION,
        this.translationService.instant('GA.BUTTONS.CANCEL'),
        'btn btn-danger',
        () => this.onCancel.emit(),
        !this.showAsDialog // hide when it's not shown as dialog
      )
    ]);
  }

  private saveCompany = ():void => {
    this.updateButtonStatus(true);
    this.wizard.validateCurrent()
      .then(() => {
        const requestData:ICompanyExtended = this.companyInfoService.createCompanyProfileRequest();
        const saveCompanyFn:Promise<ICompanyExtended> = this.companyInfoService.getCompanyId()? this.companyDataService.updateCompany(requestData) : this.companyDataService.addCompany(requestData);

        return saveCompanyFn
          .then((companyData:ICompanyExtended) => {
            this.afterSave(companyData);
            return companyData;
          });
      })
      .catch(util.noop)
      .finally(() => {
        this.updateButtonStatus(false);
      });
  };

  private afterSave(companyData:ICompanyExtended):void {
    this.messageService.showFeedbackAfterSave('GA.GENERAL.COMPANY');
    this.companyInfoService.setCompanyData(companyData);
    this.title = {
      translateText: 'APP.COMPANY.TITLE.EDIT_COMPANY',
      translateValues: { name: `- ${companyData.name}` }
    };

    this.onSaved.emit(companyData);

    if (this.companyId === CompanyInfoService.NEW_COMPANY && !this.showAsDialog) {
      this.stateService.go(STATE_COMPANYINFO, { companyId: companyData.id }, { location: 'replace' });
      return;
    }

    // notify the child controllers
    this.eventService.sendMessage(EVENT_COMPANY_UPDATED, companyData);
  }

  private updateButtonStatus = (executing:boolean):void => {
    if (executing) {
      this.wizard.setActionBusy(SAVE_ACTION, true);
      this.wizard.setActionEnabled(SAVE_ACTION, false);
    } else {
      this.wizard.setActionBusy(SAVE_ACTION, false);
      this.wizard.setActionEnabled(SAVE_ACTION, true);
    }
  };

  private getWizardTitle(companyName?:string):ITranslateText {
    return {
      translateText: this.companyInfoService.getCompanyId()? 'APP.COMPANY.TITLE.EDIT_COMPANY' : 'APP.COMPANY.TITLE.NEW_COMPANY',
      translateValues: { name: this.companyInfoService.getCompanyId() && companyName? `- ${companyName}` : '' }
    };
  }

  private clearCaches():void {
    this.wizard.clear();
    this.wizard.clearCurrentValidator();
    this.companyInfoService.clear();
    this.companyDataService.clear();
  }

  public ngOnDestroy():void {
    this.bodyClassService.remove('company-profile');
    this.clearCaches();
    this.eventHandler.unsubscribe();
  }
}